import React, { useMemo, useState } from "react";
import { Accordion, AccordionDetails, AccordionSummary, Box, Chip, Dialog, Paper, Stack } from "@mui/material";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import { FormattedDate, FormattedTime, useIntl } from "react-intl";
import { format, parseISO } from "date-fns";
import { useSnackbar } from "notistack";

import RevaText from "components/common/RevaText";
import EditMatchModal from "../../Tournament/EditMatchModal";
import { matchStage, matchNumber } from "components/common/matchIdentity";

const getParticipantName = (participant) => participant?.name || participant?.placeholder || "-";

const parseScores = (scoresCsv) => {
  const scores = scoresCsv?.split(",").filter(Boolean) || [];

  return scores.map((score) => {
    const [team1, team2] = score.split("-").map((value) => value.trim());
    return { team1, team2 };
  });
};

const MatchCard = ({ match, onClick, intl }) => {
  const participants = match.participants || [];
  const participant1 = participants[0];
  const participant2 = participants[1];
  const scores = parseScores(match.scores_csv);
  const player1Won = match.winner_id && match.winner_id === participant1?.id;
  const player2Won = match.winner_id && match.winner_id === participant2?.id;
  const message = (id, values) => intl.formatMessage({ id }, values);
  const roundLabel = `${matchStage(match, message)} · ${matchNumber(match, message)}`;

  return (
    <Paper
      elevation={0}
      sx={{
        minWidth: { xs: "100%", md: 360 },
        maxWidth: 369,
        flex: 1,
        overflow: "hidden",
        borderRadius: 2,
        cursor: "pointer",
        backgroundColor: "background.paper",
        border: (theme) => `1px solid ${theme.palette.divider}`,
        transition: "box-shadow 0.2s ease, transform 0.2s ease",
        "&:hover": {
          boxShadow: 3,
          transform: "translateY(-1px)",
        },
      }}
      onClick={onClick}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          px: 2,
          py: 1.5,
          backgroundColor: "background.gray",
          borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
        }}
      >
        <Stack gap={1} alignItems="flex-start">
          <Chip
            label={[...new Set([match.match_identity?.tournament_name || match.tournament?.name || intl.formatMessage({ id: "Torneo", defaultMessage: "Torneo" }), match.match_identity?.category_name].filter(Boolean))].join(" · ")}
            size="small"
            sx={{
              maxWidth: "100%",
              backgroundColor: "warning.dark",
              color: "warning.contrastText",
              fontWeight: 700,
              textTransform: "uppercase",
              ".MuiChip-label": {
                px: 1,
              },
            }}
          />
        </Stack>
        <RevaText variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: "uppercase" }}>
          {roundLabel}
        </RevaText>
      </Box>

      <Box sx={{ px: 2, py: 2 }}>
        {[participant1, participant2].map((participant, index) => {
          const isWinner = index === 0 ? player1Won : player2Won;
          const teamScores = scores.map((score) => (index === 0 ? score.team1 : score.team2));

          return (
            <Box
              key={participant?.id || index}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1.5,
                py: 1,
                px: 1,
                borderRadius: 1.5,
                backgroundColor: isWinner ? "action.hover" : "transparent",
                "& + &": {
                  mt: 1,
                },
              }}
            >
              <Box
                sx={{
                  minWidth: 0,
                  flex: 1,
                  borderLeft: (theme) => `3px solid ${isWinner ? theme.palette.success.main : theme.palette.divider}`,
                  pl: 1.5,
                }}
              >
                <RevaText
                  variant="body1"
                  sx={{
                    fontWeight: isWinner ? 700 : 600,
                    lineHeight: 1.2,
                    display: "-webkit-box",
                    overflow: "hidden",
                    WebkitBoxOrient: "vertical",
                    WebkitLineClamp: 2,
                  }}
                >
                  {getParticipantName(participant)}
                </RevaText>
              </Box>
              <Stack direction="row" spacing={0.75}>
                {teamScores.length > 0 ? (
                  teamScores.map((score, scoreIndex) => (
                    <Box
                      key={scoreIndex}
                      sx={{
                        minWidth: 30,
                        height: 30,
                        px: 0.75,
                        display: "grid",
                        placeItems: "center",
                        borderRadius: 1,
                        backgroundColor: "action.hover",
                      }}
                    >
                      <RevaText variant="body2" sx={{ fontWeight: 700 }}>
                        {score}
                      </RevaText>
                    </Box>
                  ))
                ) : (
                  <RevaText variant="caption" color="text.secondary">
                    -
                  </RevaText>
                )}
              </Stack>
            </Box>
          );
        })}
      </Box>

      <Stack gap={1} alignItems="flex-start" sx={{ px: 2, pb: 1.5 }}>
        {match.space?.name && (
          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0 }}>
            <PlaceOutlinedIcon sx={{ fontSize: 16, color: "text.secondary" }} />
            <RevaText variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
              {match.space.name}
            </RevaText>
          </Stack>
        )}
      </Stack>
    </Paper>
  );
};

const ScheduleByDay = ({ matches, refresh }) => {
  const { enqueueSnackbar } = useSnackbar();
  const intl = useIntl();
  const [selectedMatch, setSelectedMatch] = useState(null);

  const handleMatchUpdated = () => {
    enqueueSnackbar(intl.formatMessage({ id: "Partido actualizado" }), {
      variant: "success",
    });
    setSelectedMatch(null);
    if (refresh) refresh();
  };
  const scheduledMatches = useMemo(() => {
    return matches.filter((g) => g.is_scheduled);
  }, [matches]);

  const grouped = useMemo(() => {
    const result = {};

    scheduledMatches.forEach((match) => {
      const scheduledAt = parseISO(match.scheduled_at);
      const dayKey = format(scheduledAt, "yyyy-MM-dd");
      const timeKey = format(scheduledAt, "HH:mm");

      if (!result[dayKey]) {
        result[dayKey] = {};
      }

      if (!result[dayKey][timeKey]) {
        result[dayKey][timeKey] = [];
      }

      result[dayKey][timeKey].push(match);
    });

    return result;
  }, [scheduledMatches]);

  const sortedDates = Object.keys(grouped).sort();

  return (
    <Box sx={{ display: "grid", gap: 3 }}>
      {sortedDates.map((dateKey) => (
        <Accordion
          key={dateKey}
          defaultExpanded={false}
          disableGutters
          elevation={0}
          sx={{ background: "transparent" }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 0 }}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Box sx={{ width: 4, height: 28, borderRadius: 999, backgroundColor: "success.main" }} />
              <CalendarMonthOutlinedIcon sx={{ color: "success.main" }} />
              <RevaText variant="h5" sx={{ fontWeight: 700 }}>
                <FormattedDate value={parseISO(dateKey)} weekday="long" month="long" day="numeric" />
              </RevaText>
            </Stack>
          </AccordionSummary>

          <AccordionDetails sx={{ px: 0, pt: 0 }}>
            <Box sx={{ display: "grid", gap: 2.5 }}>
              {Object.keys(grouped[dateKey])
                .sort()
                .map((timeKey) => (
                  <Box
                    key={`${dateKey}-${timeKey}`}
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "90px minmax(0, 1fr)" },
                      alignItems: "start",
                    }}
                  >
                    <Box sx={{ pt: { md: 0.5 } }}>
                      <RevaText variant="h6" color="text.secondary" sx={{ fontWeight: 700 }}>
                        <FormattedTime value={parseISO(`${dateKey}T${timeKey}:00`)} hour="2-digit" minute="2-digit" />
                      </RevaText>
                    </Box>

                    <Box
                      sx={{
                        pl: { md: 2.5 },
                        borderLeft: (theme) => `1px solid ${theme.palette.divider}`,
                      }}
                    >
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                        {grouped[dateKey][timeKey]
                          .sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at))
                          .map((game) => (
                            <MatchCard key={game.id} match={game} intl={intl} onClick={() => setSelectedMatch(game)} />
                          ))}
                      </Box>
                    </Box>
                  </Box>
                ))}
            </Box>
          </AccordionDetails>
        </Accordion>
      ))}
      <Dialog
        open={Boolean(selectedMatch)}
        onBackdropClick={() => setSelectedMatch(null)}
        disableAutoFocus
        fullWidth={false}
        maxWidth="md"
      >
        {selectedMatch && (
          <EditMatchModal
            matchId={selectedMatch.id}
            tabName="details"
            canEdit
            isBracketStage={selectedMatch.tournament?.state === "underway"}
            showScoreTab={Boolean(selectedMatch.participants?.[0]?.id && selectedMatch.participants?.[1]?.id)}
            onSubmitScore={handleMatchUpdated}
          />
        )}
      </Dialog>
    </Box>
  );
};

export default ScheduleByDay;
